// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from '../services/auth-service';
import { ALL_ORGS_ID } from '../types/group';
import { ConfigurationService } from '../services/configuration-service';
import { Event, NavigationStart, Router } from '@angular/router';
import { withLatestFrom } from 'rxjs/operators';

export interface IContextHelpMenuItem {
  token: string;
  url: string;
}

@Injectable({
  providedIn: 'root'
})
export class HeaderService {
  public title = new BehaviorSubject('');
  public subtitle = new BehaviorSubject('');
  public icon = new BehaviorSubject('Icon');
  public username = new BehaviorSubject('Username');
  public navMenuText = new BehaviorSubject('');
  public userFilter = new BehaviorSubject(false);
  public userFilterDisabled = new BehaviorSubject(false);
  public orgSelectedChanged: BehaviorSubject<string> = new BehaviorSubject<string>(ALL_ORGS_ID);
  public orgSelectorDefaultOption = new BehaviorSubject('All Organizations');
  public contextSensitiveHelp$: BehaviorSubject<IContextHelpMenuItem[]> = new BehaviorSubject([]);
  public showRefreshButton$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public refresh$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private oldRoute = '';

  constructor(private authService: AuthService,
              private readonly configurationService: ConfigurationService,
              private readonly router: Router) {
      this.authService.isLoggedIn$.subscribe(isLoggedIn => {
        if (isLoggedIn) {
          this.resetOnRoute();
        }
        this.setOrgSelectedChanged(ALL_ORGS_ID);
        this.setOrgSelectorDefaultOption('All Organizations');
        this.setUsername('Username');
        this.showUserFilter(false);
      });
    router.events
      .pipe(withLatestFrom(this.authService.isLoggedIn$))
      .subscribe(([event, isLoggedIn]: [Event, boolean]) => {
        if (event instanceof NavigationStart) {
          if (isLoggedIn) {
            const newRoute = event.url.split("?")[0];
            if (newRoute != this.oldRoute) {
              this.resetOnRoute();
            }
            this.oldRoute = newRoute;
          }
        }
      });
  }

  resetOnRoute() {
    this.setTitle('');
    this.setSubtitle('');
    this.setIcon('Icon');
    this.setNavMenuText('');
    this.setContextHelp([]);
    this.showRefreshButton$.next(false);
  }

  setTitle(title) {
    this.title.next(title);
  }

  setSubtitle(subtitle) {
    this.subtitle.next(subtitle);
  }

  setNavMenuText(text) {
    this.navMenuText.next(text);
  }

  setIcon(icon) {
    this.icon.next(icon);
  }

  setUsername(username) {
    this.username.next(username);
  }

  showUserFilter(enable: boolean) {
    this.userFilter.next(enable);
    this.userFilterDisabled.next(false);
  }

  disableUserFilter() {
    this.userFilterDisabled.next(true);
  }

  setOrgSelectedChanged(value: string) {
    if (this.authService.isLoggedIn) {
      this.orgSelectedChanged.next(value);
    }
  }

  setOrgSelectorDefaultOption(value: string) {
    this.orgSelectorDefaultOption.next(value);
  }

  setContextHelp(tokens: string[]) {
    const defaultUrl = this.configurationService.defaultHelpUrl;
    const contextHelpMap = this.configurationService.contextHelpMap;
    const items: IContextHelpMenuItem[] = tokens.map(token => {
      const url = contextHelpMap[token] || defaultUrl;
      return {token, url};
    });
    this.contextSensitiveHelp$.next(items);
  }

  showRefreshButton(enabled: boolean) {
    this.showRefreshButton$.next(enabled);
  }
}
